// Second Brain / Semantic Memory Service for Way2Will
// Provides persistent vector-based knowledge storage with semantic retrieval
// Uses Local Similarity Search with Gemini Embeddings

import { db } from '../config/firebase';
import { isFirestoreAvailable } from './firestoreService';
import {
    collection,
    doc,
    setDoc,
    getDocs,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    updateDoc,
    increment,
} from 'firebase/firestore';
import {
    generateDocumentEmbedding,
    generateQueryEmbedding,
    cosineSimilarity,
    isEmbeddingAvailable,
} from './embeddingService';

// ============ TYPES ============

export type MemoryType = 'fact' | 'win' | 'milestone' | 'preference' | 'goal' | 'habit_achievement' | 'conversation';
export type MemoryCategory = 'personal' | 'work' | 'health' | 'financial' | 'relationship' | 'general';
export type MemorySource = 'debrief' | 'conversation' | 'manual' | 'habit' | 'goal';
export type ImportanceLevel = 'high' | 'medium' | 'low';

// Persona relevance scores for targeted retrieval
export interface PersonaRelevance {
    coach: number;    // 0-1
    mentor: number;
    friend: number;
    executive: number;
    zen: number;
    cool: number;     // "The Homie" - Orus
}

// Habit-specific metadata
export interface HabitMemoryData {
    habitId: string;
    habitTitle: string;
    streakLength?: number;
    context?: string;  // e.g., "4-day-a-week gym routine"
}

// Core vector memory entry stored in Firestore
export interface VectorMemoryEntry {
    id: string;
    userId: string;

    // Content
    type: MemoryType;
    title: string;
    content: string;
    summary?: string;

    // Categorization
    category: MemoryCategory;
    tags: string[];

    // Vector embedding (768 dimensions for gemini-embedding-001)
    embedding: number[];

    // Metadata
    source: MemorySource;
    sourceDate: string;  // YYYY-MM-DD of original event
    createdAt: Timestamp;
    updatedAt: Timestamp;

    // Relevance scoring
    importance: ImportanceLevel;
    accessCount: number;
    lastAccessed?: Timestamp;

    // Optional persona-specific relevance
    personaRelevance?: PersonaRelevance;

    // Optional habit-specific data
    habitData?: HabitMemoryData;
}

// Input for creating a new memory (before embedding)
export interface CreateMemoryInput {
    type: MemoryType;
    title: string;
    content: string;
    summary?: string;
    category: MemoryCategory;
    tags: string[];
    source: MemorySource;
    sourceDate: string;
    importance: ImportanceLevel;
    personaRelevance?: PersonaRelevance;
    habitData?: HabitMemoryData;
}

// Search result with similarity score
export interface MemorySearchResult extends VectorMemoryEntry {
    similarity: number;
}

// Key fact extracted from debriefs
export interface ExtractedFact {
    type: MemoryType;
    title: string;
    content: string;
    category: MemoryCategory;
    importance: ImportanceLevel;
    tags: string[];
    habitData?: HabitMemoryData;
}

// ============ STORAGE KEYS ============

const MEMORIES_COLLECTION = 'memories';
const LOCAL_CACHE_KEY = 'way2will_vector_memories_cache';

// ============ HELPER FUNCTIONS ============

function getMemoriesCollection(userId: string) {
    if (!db) throw new Error('Firestore not initialized');
    return collection(db, 'users', userId, MEMORIES_COLLECTION);
}

/**
 * Maps a Firestore document to a VectorMemoryEntry
 */
function mapFirestoreDocToMemory(docData: any): VectorMemoryEntry {
    const data = docData;
    const embedding = data.embedding;

    return {
        ...data,
        embedding: Array.isArray(embedding) ? embedding : [],
    } as VectorMemoryEntry;
}

// Local cache for fallback when Firestore unavailable
function getCachedMemories(userId: string): VectorMemoryEntry[] {
    try {
        const cached = localStorage.getItem(`${LOCAL_CACHE_KEY}_${userId}`);
        return cached ? JSON.parse(cached) : [];
    } catch {
        return [];
    }
}

function setCachedMemories(userId: string, memories: VectorMemoryEntry[]): void {
    try {
        // Only cache last 100 memories to avoid storage limits
        const toCache = memories.slice(0, 100);
        localStorage.setItem(`${LOCAL_CACHE_KEY}_${userId}`, JSON.stringify(toCache));
    } catch (e) {
        console.warn('[SecondBrain] Cache write failed:', e);
    }
}

// ============ CORE FUNCTIONS ============

/**
 * Save a new memory with auto-generated embedding
 */
export async function saveVectorMemory(
    userId: string,
    input: CreateMemoryInput
): Promise<VectorMemoryEntry> {
    if (!userId) throw new Error('userId required');

    // Generate embedding from content
    const textToEmbed = `${input.title}. ${input.content}`;
    let embedding: number[] = [];

    if (isEmbeddingAvailable()) {
        try {
            embedding = await generateDocumentEmbedding(textToEmbed);
        } catch (e) {
            console.error('[SecondBrain] Embedding failed, saving without vector:', e);
        }
    }

    const now = Timestamp.now();
    const memoryId = `mem_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const entry: VectorMemoryEntry = {
        id: memoryId,
        userId,
        ...input,
        embedding,
        createdAt: now,
        updatedAt: now,
        accessCount: 0,
    };

    // Save to Firestore if available
    if (isFirestoreAvailable() && db) {
        try {
            const memoriesRef = getMemoriesCollection(userId);
            await setDoc(doc(memoriesRef, memoryId), entry);
            console.log(`[SecondBrain] Saved memory: ${input.title}`);
        } catch (e) {
            console.error('[SecondBrain] Firestore save failed:', e);
        }
    }

    // Update local cache
    const cached = getCachedMemories(userId);
    cached.unshift(entry);
    setCachedMemories(userId, cached);

    return entry;
}

/**
 * Search memories using semantic similarity
 * Returns top K most relevant memories using Local Similarity Search
 */
export async function searchSemanticMemories(
    userId: string,
    queryText: string,
    limitCount: number = 3
): Promise<MemorySearchResult[]> {
    if (!userId || !queryText) return [];

    console.log(`[SecondBrain] Searching for: "${queryText.slice(0, 50)}..."`);

    // Generate query embedding
    let queryEmbedding: number[] = [];
    if (isEmbeddingAvailable()) {
        try {
            queryEmbedding = await generateQueryEmbedding(queryText);
        } catch (e) {
            console.error('[SecondBrain] Query embedding failed:', e);
            // Fall back to keyword search
            return searchMemoriesByKeyword(userId, queryText, limitCount);
        }
    } else {
        return searchMemoriesByKeyword(userId, queryText, limitCount);
    }

    // Use local similarity search (Firestore Vector Search is disabled in this build)
    // Get last 100 memories and do manual similarity
    let memories: VectorMemoryEntry[] = [];
    if (isFirestoreAvailable() && db) {
        try {
            const memoriesRef = getMemoriesCollection(userId);
            const q = query(
                memoriesRef,
                orderBy('createdAt', 'desc'),
                limit(100)
            );
            const snapshot = await getDocs(q);
            snapshot.forEach(doc => {
                const data = mapFirestoreDocToMemory(doc.data());
                if (data.embedding && data.embedding.length > 0) {
                    memories.push(data);
                }
            });
        } catch (e) {
            console.error('[SecondBrain] Firestore fallback query failed:', e);
            memories = getCachedMemories(userId);
        }
    } else {
        memories = getCachedMemories(userId);
    }

    if (memories.length === 0) return [];

    // Calculate similarity scores
    const scored: MemorySearchResult[] = memories
        .map(memory => ({
            ...memory,
            similarity: cosineSimilarity(queryEmbedding, memory.embedding),
        }));

    const results = scored
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, limitCount);

    console.log(`[SecondBrain] Found ${results.length} memories via manual similarity`);

    for (const result of results) {
        updateMemoryAccess(userId, result.id).catch(() => { });
    }

    return results;
}

/**
 * Fallback keyword-based search when embeddings unavailable
 */
export async function searchMemoriesByKeyword(
    userId: string,
    queryText: string,
    limitCount: number = 3
): Promise<MemorySearchResult[]> {
    const queryLower = queryText.toLowerCase();
    const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);

    let memories: VectorMemoryEntry[] = [];

    if (isFirestoreAvailable() && db) {
        try {
            const memoriesRef = getMemoriesCollection(userId);
            const q = query(memoriesRef, orderBy('createdAt', 'desc'), limit(50));
            const snapshot = await getDocs(q);
            snapshot.forEach(doc => memories.push(mapFirestoreDocToMemory(doc.data())));
        } catch {
            memories = getCachedMemories(userId);
        }
    } else {
        memories = getCachedMemories(userId);
    }

    // Score by keyword overlap
    const scored = memories.map(memory => {
        let score = 0;
        const titleLower = memory.title.toLowerCase();
        const contentLower = memory.content.toLowerCase();

        for (const word of queryWords) {
            if (titleLower.includes(word)) score += 2;
            if (contentLower.includes(word)) score += 1;
            if (memory.tags.some(t => t.toLowerCase().includes(word))) score += 1.5;
        }

        // Importance bonus
        if (memory.importance === 'high') score += 1;

        return { ...memory, similarity: score / 10 }; // Normalize
    });

    return scored
        .filter(m => m.similarity > 0)
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, limitCount);
}

/**
 * Update access count for a memory (for relevance tracking)
 */
export async function updateMemoryAccess(userId: string, memoryId: string): Promise<void> {
    if (!isFirestoreAvailable() || !db) return;

    try {
        const memoryRef = doc(db, 'users', userId, MEMORIES_COLLECTION, memoryId);
        await updateDoc(memoryRef, {
            accessCount: increment(1),
            lastAccessed: Timestamp.now(),
        });
    } catch (e) {
        console.warn('[SecondBrain] Access count update failed:', e);
    }
}

/**
 * Delete a memory
 */
export async function deleteMemory(userId: string, memoryId: string): Promise<void> {
    if (isFirestoreAvailable() && db) {
        try {
            const memoryRef = doc(db, 'users', userId, MEMORIES_COLLECTION, memoryId);
            await deleteDoc(memoryRef);
        } catch (e) {
            console.error('[SecondBrain] Delete failed:', e);
        }
    }

    // Update cache
    const cached = getCachedMemories(userId);
    const filtered = cached.filter(m => m.id !== memoryId);
    setCachedMemories(userId, filtered);
}

/**
 * Get all memories for a user (for admin/debug)
 */
export async function getAllMemories(userId: string): Promise<VectorMemoryEntry[]> {
    if (!isFirestoreAvailable() || !db) {
        return getCachedMemories(userId);
    }

    try {
        const memoriesRef = getMemoriesCollection(userId);
        const q = query(memoriesRef, orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);

        const memories: VectorMemoryEntry[] = [];
        snapshot.forEach(doc => memories.push(mapFirestoreDocToMemory(doc.data())));

        return memories;
    } catch (e) {
        console.error('[SecondBrain] Get all failed:', e);
        return getCachedMemories(userId);
    }
}

/**
 * Get memories by type
 */
export async function getMemoriesByType(
    userId: string,
    type: MemoryType
): Promise<VectorMemoryEntry[]> {
    if (!isFirestoreAvailable() || !db) {
        return getCachedMemories(userId).filter(m => m.type === type);
    }

    try {
        const memoriesRef = getMemoriesCollection(userId);
        const q = query(
            memoriesRef,
            where('type', '==', type),
            orderBy('createdAt', 'desc'),
            limit(20)
        );
        const snapshot = await getDocs(q);

        const memories: VectorMemoryEntry[] = [];
        snapshot.forEach(doc => memories.push(mapFirestoreDocToMemory(doc.data())));
        return memories;
    } catch (e) {
        console.error('[SecondBrain] Type query failed:', e);
        return getCachedMemories(userId).filter(m => m.type === type);
    }
}

/**
 * Get habit-related memories (for Orus authentic encouragement)
 */
export async function getHabitMemories(userId: string): Promise<VectorMemoryEntry[]> {
    if (!isFirestoreAvailable() || !db) {
        return getCachedMemories(userId).filter(m => m.type === 'habit_achievement');
    }

    try {
        const memoriesRef = getMemoriesCollection(userId);
        const q = query(
            memoriesRef,
            where('type', '==', 'habit_achievement'),
            orderBy('createdAt', 'desc'),
            limit(10)
        );
        const snapshot = await getDocs(q);

        const memories: VectorMemoryEntry[] = [];
        snapshot.forEach(doc => memories.push(mapFirestoreDocToMemory(doc.data())));
        return memories;
    } catch (e) {
        console.error('[SecondBrain] Habit query failed:', e);
        return getCachedMemories(userId).filter(m => m.type === 'habit_achievement');
    }
}

// ============ KEY FACT EXTRACTION ============

/**
 * Extract key facts from evening debrief data
 * Uses pattern matching and heuristics
 */
export function extractKeyFactsFromDebrief(debriefData: {
    date: string;
    rating: number;
    wins: string[];
    improvements: string[];
    gratitude: string[];
    journal: string;
}): ExtractedFact[] {
    const facts: ExtractedFact[] = [];
    const { date, rating, wins, improvements, gratitude, journal } = debriefData;

    // Extract wins as achievements
    for (const win of wins) {
        if (win.trim().length < 5) continue;

        const fact: ExtractedFact = {
            type: 'win',
            title: win.slice(0, 50),
            content: win,
            category: categorizeText(win),
            importance: rating >= 4 ? 'high' : 'medium',
            tags: extractTags(win),
        };

        // Check for habit-related wins
        const habitData = detectHabitAchievement(win);
        if (habitData) {
            fact.type = 'habit_achievement';
            fact.habitData = habitData;
        }

        facts.push(fact);
    }

    // Extract improvements as goals
    for (const improvement of improvements) {
        if (improvement.trim().length < 5) continue;

        facts.push({
            type: 'goal',
            title: `Goal: ${improvement.slice(0, 40)}`,
            content: improvement,
            category: categorizeText(improvement),
            importance: 'medium',
            tags: ['improvement', 'goal', ...extractTags(improvement)],
        });
    }

    // Extract significant journal entries
    if (journal && journal.length > 50) {
        const milestones = detectMilestones(journal);
        for (const milestone of milestones) {
            facts.push({
                type: 'milestone',
                title: milestone.title,
                content: milestone.content,
                category: milestone.category,
                importance: 'high',
                tags: ['journal', 'milestone', ...milestone.tags],
            });
        }
    }

    // Extract meaningful gratitude entries (for emotional context)
    const meaningfulGratitude = gratitude.filter(g => g && g.trim().length > 10);
    if (meaningfulGratitude.length >= 2) {
        facts.push({
            type: 'preference',
            title: `Grateful for: ${meaningfulGratitude[0].slice(0, 30)}`,
            content: `On ${date}, expressed gratitude for: ${meaningfulGratitude.join('; ')}`,
            category: 'personal',
            importance: 'low',
            tags: ['gratitude', 'emotional', 'reflection'],
        });
    }

    // High rating days are memorable
    if (rating === 5 && wins.length > 0) {
        facts.push({
            type: 'milestone',
            title: `5-star day: ${date}`,
            content: `Had an amazing day with ${wins.length} wins: ${wins.slice(0, 3).join(', ')}`,
            category: 'personal',
            importance: 'high',
            tags: ['achievement', '5-star', 'great-day'],
        });
    }

    console.log(`[SecondBrain] Extracted ${facts.length} facts from debrief`);
    return facts.slice(0, 5); // Max 5 facts per debrief
}

// Helper: Categorize text based on keywords
function categorizeText(text: string): MemoryCategory {
    const lower = text.toLowerCase();

    if (lower.match(/gym|workout|exercise|run|health|sleep|diet|weight/)) return 'health';
    if (lower.match(/work|project|meeting|client|deadline|team|boss/)) return 'work';
    if (lower.match(/money|budget|save|invest|cost|pay|salary|\$/)) return 'financial';
    if (lower.match(/family|friend|partner|relationship|love|date/)) return 'relationship';

    return 'personal';
}

// Helper: Extract meaningful tags from text
function extractTags(text: string): string[] {
    const tags: string[] = [];
    const lower = text.toLowerCase();

    // Activity tags
    if (lower.match(/gym|workout|exercise/)) tags.push('fitness');
    if (lower.match(/read|book/)) tags.push('reading');
    if (lower.match(/meditat|mindful/)) tags.push('meditation');
    if (lower.match(/work|project/)) tags.push('work');
    if (lower.match(/learn|study|course/)) tags.push('learning');

    return tags;
}

// Helper: Detect habit achievements with streak info
function detectHabitAchievement(text: string): HabitMemoryData | null {
    const lower = text.toLowerCase();

    // Pattern: "X days/times" streak
    const streakMatch = lower.match(/(\d+)\s*(days?|times?|weeks?)\s*(in a row|straight|streak)?/);

    // Gym-specific patterns
    if (lower.match(/gym|workout|exercise/)) {
        return {
            habitId: 'gym',
            habitTitle: 'Gym workout',
            streakLength: streakMatch ? parseInt(streakMatch[1]) : undefined,
            context: extractHabitContext(text, 'gym'),
        };
    }

    // Reading patterns
    if (lower.match(/read|book|reading/)) {
        return {
            habitId: 'reading',
            habitTitle: 'Reading',
            streakLength: streakMatch ? parseInt(streakMatch[1]) : undefined,
            context: extractHabitContext(text, 'reading'),
        };
    }

    // Meditation patterns
    if (lower.match(/meditat/)) {
        return {
            habitId: 'meditation',
            habitTitle: 'Meditation',
            streakLength: streakMatch ? parseInt(streakMatch[1]) : undefined,
            context: extractHabitContext(text, 'meditation'),
        };
    }

    return null;
}

// Helper: Extract habit context like "4-day-a-week routine"
function extractHabitContext(text: string, habitType: string): string {
    const lower = text.toLowerCase();

    // Pattern: "X times/days a week"
    const weeklyMatch = lower.match(/(\d+)\s*(times?|days?)\s*(a|per)\s*week/);
    if (weeklyMatch) {
        return `${weeklyMatch[1]}-day-a-week ${habitType} routine`;
    }

    // Pattern: "daily X"
    if (lower.match(/daily|every\s*day/)) {
        return `daily ${habitType} practice`;
    }

    return `${habitType} habit`;
}

// Helper: Detect milestones in journal text
function detectMilestones(journal: string): Array<{ title: string; content: string; category: MemoryCategory; tags: string[] }> {
    const milestones: Array<{ title: string; content: string; category: MemoryCategory; tags: string[] }> = [];
    const lower = journal.toLowerCase();

    // Financial milestones
    const moneyMatch = lower.match(/saved?\s*\$?(\d+(?:,\d{3})*(?:\.\d{2})?)/);
    if (moneyMatch) {
        milestones.push({
            title: `Saved $${moneyMatch[1]}`,
            content: journal.slice(0, 200),
            category: 'financial',
            tags: ['savings', 'milestone', 'money'],
        });
    }

    // Completion milestones
    const completionMatch = lower.match(/(finally|completed|finished|done with)\s+(.{10,50})/);
    if (completionMatch) {
        milestones.push({
            title: `Completed: ${completionMatch[2].slice(0, 40)}`,
            content: journal.slice(0, 200),
            category: categorizeText(completionMatch[2]),
            tags: ['completion', 'milestone'],
        });
    }

    return milestones;
}

/**
 * Save extracted facts from a debrief to the vector store
 */
export async function saveDebriefFacts(
    userId: string,
    debriefData: {
        date: string;
        rating: number;
        wins: string[];
        improvements: string[];
        gratitude: string[];
        journal: string;
    }
): Promise<VectorMemoryEntry[]> {
    const facts = extractKeyFactsFromDebrief(debriefData);
    const savedMemories: VectorMemoryEntry[] = [];

    for (const fact of facts) {
        try {
            const memory = await saveVectorMemory(userId, {
                ...fact,
                source: 'debrief',
                sourceDate: debriefData.date,
            });
            savedMemories.push(memory);
        } catch (e) {
            console.error('[SecondBrain] Failed to save fact:', e);
        }
    }

    console.log(`[SecondBrain] Saved ${savedMemories.length} facts from debrief`);
    return savedMemories;
}

// ============ BACKWARD COMPATIBILITY ============

/**
 * Generate AI context string from stored memories (for text-based AI service)
 * This is a synchronous fallback for the legacy aiService.ts
 */
export function generateAIContext(userQuery: string): string {
    // Note: This is a simplified synchronous version for backward compatibility
    // The full semantic search is async and used by the voice assistant

    const queryLower = userQuery.toLowerCase();

    // Check local cache for relevant memories
    // We can't do async search here, so use cached data if available
    const cacheKey = Object.keys(localStorage).find(k => k.startsWith('way2will_vector_memories_cache_'));
    if (!cacheKey) return '';

    try {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return '';

        const memories: VectorMemoryEntry[] = JSON.parse(cached);
        if (!memories || memories.length === 0) return '';

        // Simple keyword matching for sync context
        const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);

        const relevant = memories
            .filter(m => {
                const titleMatch = queryWords.some(w => m.title.toLowerCase().includes(w));
                const contentMatch = queryWords.some(w => m.content.toLowerCase().includes(w));
                const tagMatch = m.tags.some(t => queryWords.some(w => t.includes(w)));
                return titleMatch || contentMatch || tagMatch;
            })
            .slice(0, 3);

        if (relevant.length === 0) return '';

        const contextLines = relevant.map(m =>
            `• [${m.sourceDate}] ${m.title}: ${m.content.slice(0, 100)}${m.content.length > 100 ? '...' : ''}`
        );

        return `\n\nRELEVANT MEMORIES:\n${contextLines.join('\n')}`;
    } catch {
        return '';
    }
}

